import { Button, Form, Input, Space } from 'antd';
import type { Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../Core/Icon';

export interface FilePublicPasswordFormProps {
  /**
   * Called with the access code on submit.
   * Handles form submission result.
   */
  onSubmit: (accessCode: string) => void | Promise<void>;
  /**
   * Optional. If provided, renders a Cancel button, useful for modal use.
   */
  onCancel?: () => void;
  /**
   * Optional error message shown below the input field if present.
   */
  error?: string | null;
  /**
   * Disables submit button and shows loading indicator when true.
   */
  loading?: boolean;
  /**
   * Provides a ref to the password input for focus/select methods (e.g., on error).
   */
  inputRef?: Ref<{ focus: () => void; select: () => void }>;

  compact?: boolean;
}

/**
 * FilePublicPasswordForm
 *
 * A reusable form for entering an access code (password) for password-protected public file links.
 * Used both in the Viewer (Ant Design modal) and the FilePublicLink full-page password prompt.
 */
export function FilePublicPasswordForm({
  onSubmit,
  onCancel,
  error = null,
  loading = false,
  inputRef,
  compact = false,
}: FilePublicPasswordFormProps) {
  const { t } = useTranslation();
  // Ant Design Form instance for managing form operations and validation
  const [form] = Form.useForm<{ accessCode: string }>();

  /**
   * Handles final form submission. Trims the access code before invoking the callback.
   * @param values - Form values including accessCode
   */
  const handleFinish = (values: { accessCode: string }) => {
    onSubmit(values.accessCode.trim());
  };

  return (
    <div className={'pt-6'}>
      {/* Show a context description in non-compact mode */}

      <p className="text-gray-dark mb-4 text-sm">{t('common.protectedContentMessage')}</p>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        className={'w-full! [&_.ant-form-item]:mb-2! [&_.ant-form-item-explain-error]:hidden!'}
      >
        <Form.Item
          name="accessCode"
          // Shows red border and error message if error is supplied
          validateStatus={error ? 'error' : undefined}
          help={error ?? undefined}
          rules={[{ required: true, message: t('common.accessCodeRequired') }]}
        >
          {/* Use AntD+Tailwind input group for a "key" icon in front of password box */}
          <Space.Compact className="w-full!">
            <Space.Addon>
              <Icon name="KeyIcon" className="h-5 w-5" />
            </Space.Addon>
            <Input.Password
              ref={inputRef as any}
              placeholder={t('common.accessCodePlaceholder')}
              autoComplete="off"
              className="w-full!"
              disabled={loading}
            />
          </Space.Compact>
        </Form.Item>
        {/* Button group: Cancel (if supplied) and Access/Submit */}
        <div className={compact ? 'mt-4 flex justify-end gap-2' : 'mt-4!'}>
          {onCancel && (
            <Button onClick={onCancel} disabled={loading}>
              {t('common.cancel')}
            </Button>
          )}
          <Button type="primary" htmlType="submit" block={!onCancel} loading={loading}>
            {t('common.access')}
          </Button>
        </div>
      </Form>
    </div>
  );
}
